import HeaderLayout from '~/components/ui/HeaderLayout';
import { Link, useSearchParams } from 'react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/Card';
import { Avatar, AvatarImage } from '~/components/ui/Avatar';
import {
  googleBusinessProfile,
  googleCTA,
} from '~/assets/connectors/connectorLogo';
import { Button } from '~/components/ui/Button';
import { Separator } from '~/components/ui/Separator';
import { apiClient } from '~/lib/apiClient';
import { useToast } from '~/hooks/use-toast';
import { useEffect, useMemo, useState } from 'react';
import { CheckIcon, RefreshCcw } from 'lucide-react';
import { Spinner } from '~/components/ui/spinner';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/Breadcrumb';
import ConnectionSuccessSection from '~/components/ui/ConnectionSuccessSection';
import ConnectorDocumentation from '~/components/connector/ConnectorDocumentation';
import { type SetupGuideData } from '~/components/connector/ConnectorSetupGuide';
import { type CatalogueTable } from '~/components/connector/ConnectorDataCatalogue';
import { useActiveTeam } from '~/stores/useTeamStore';
import { useAuthStore } from '~/stores/useAuthStore';
const googleBusinessProfileSetupGuide: SetupGuideData = {
  prerequisites: [
    'An active Google Business Profile with Owner or Manager role on the business location(s) you want to sync',
  ],
  steps: [
    {
      title: 'Authenticate with Google',
      description:
        'Click "Continue with Google" to grant DViO access to your Business Profile data',
      note: "You will be redirected to Google's secure OAuth flow — DViO One will request permission to read your Business Profile locations and performance data",
    },
    {
      title: 'Complete Connection',
      description:
        'Click "Connect Account" to establish the connection and begin data synchronization',
      note: 'Business performance data will be synced automatically',
    },
  ],
};

const googleBusinessProfileCatalogue: CatalogueTable[] = [
  {
    name: 'Locations',
    keyFields: [
      'ACCOUNTID',
      'LOCATIONID',
      'TITLE',
      'STORECODE',
      'WEBSITEURI',
      'LATLNGLATITUDE',
      'LATLNGLONGITUDE',
      'RATINGS',
      'REVIEWS',
    ],
    totalFields: 38,
    note: 'Comprehensive location data including business details, address, coordinates, ratings, reviews, and metadata',
  },
  {
    name: 'Daily Metrics',
    keyFields: [
      'LOCATIONNAME',
      'DATE',
      'BUSINESSIMPRESSIONSDESKTOPMAPS',
      'BUSINESSIMPRESSIONSMOBILEMAPS',
      'DIRECTIONCLICKS',
      'WEBSITECLICKS',
      'CALLCLICKS',
      'BUSINESSBOOKINGS',
    ],
    totalFields: 10,
    note: 'Daily performance metrics tracking impressions across desktop and mobile platforms, user interactions, and conversion actions',
  },
  {
    name: 'Categories',
    keyFields: [
      'DISPLAYNAME',
      'NAME',
      'MOREHOURSTYPESDISPLAYNAME',
      'MOREHOURSTYPESHOURSTYPEID',
      'SERVICETYPESDISPLAYNAME',
      'SERVICETYPESSERVICETYPEID',
    ],
    totalFields: 7,
    note: 'Business category classifications including primary categories, service types, and extended hours configurations',
  },
  {
    name: 'Attributes',
    keyFields: [
      'DISPLAYNAME',
      'GROUPDISPLAYNAME',
      'PARENT',
      'VALUEMETADATADISPLAYNAME',
      'VALUEMETADATAVALUE',
      'VALUETYPE',
    ],
    totalFields: 7,
    note: 'Business attributes and features such as amenities, accessibility options, and service characteristics',
  },
  {
    name: 'Accounts',
    keyFields: [
      'ACCOUNTID',
      'ACCOUNTNAME',
      'TYPE',
      'VERIFICATIONSTATE',
      'VETTEDSTATE',
    ],
    totalFields: 5,
    note: 'Google Business Profile account information including verification and vetting status',
  },
];

export default function GoogleBusinessProfile() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [secretId, setSecretId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isReauthFlow, setIsReauthFlow] = useState<boolean>(false);
  const activeTeam = useActiveTeam();
  const refreshTeams = useAuthStore((state) => state.refreshTeams);
  const connectionStatus = useMemo(() => {
    return activeTeam?.connections?.['google-business-profile'];
  }, [activeTeam?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSecretId(null);
    setSuccess(false);
    setIsReauthFlow(false);
  }, [activeTeam?.id]);

  useEffect(() => {
    const secretIdParam = searchParams.get('code');
    const isReauth = sessionStorage.getItem('reauth_google_business') === 'true';

    if (secretIdParam) {
      setSecretId(secretIdParam);
      setIsReauthFlow(isReauth);
    }
  }, [searchParams]);

  const initiateConnection = async (isReauth = false) => {
    setLoading(true);
    setError(null);
    if (isReauth) {
      sessionStorage.setItem('reauth_google_business', 'true');
    } else {
      sessionStorage.removeItem('reauth_google_business');
    }

    try {
      const data = await apiClient.get<{ detail?: string } | string>(
        `/connectors/custom/google-business-login`,
      );

      if (typeof data === 'object' && data !== null && 'detail' in data) {
        setError(data?.detail || 'Authorization Failed');
      } else if (typeof data === 'string' && data !== null) {
        window.location.href = `${data}&force_login=true`;
      }
    } catch (err) {
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const establishConnection = async () => {
    if (!secretId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.post<{
        message?: string;
        detail?: string;
      }>('/connectors/custom/google-business-connection', {
        auth_code: secretId,
      });

      if (data.detail) {
        setError(data?.detail || 'Account connection failed');
      } else {
        setSuccess(true);
        await refreshTeams();
        toast({
          title: 'Success',
          description: data.message || 'Account connected successfully',
        });
      }
    } catch (err) {
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const reestablishConnection = async () => {
    if (!secretId) return;

    setLoading(true);
    setError(null);
    sessionStorage.removeItem('reauth_google_business');

    try {
      const data = await apiClient.patch<{
        message?: string;
        detail?: string;
      }>('/connectors/custom/google-business-connection', {
        auth_code: secretId,
      });

      if (data.detail) {
        setError(data?.detail || 'Account connection failed');
      } else {
        setSuccess(true);
        await refreshTeams();
        toast({
          title: 'Success',
          description: data.message || 'Account re-authenticated successfully',
        });
      }
    } catch (err) {
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (error) {
      toast({
        title: 'Failed',
        duration: 3000,
        variant: 'destructive',
        description: error,
      });
      setError(null);
    }
  }, [error, toast]);

  const renderAuthenticationStep = () => (
    <div className="space-y-5">
      <div className="space-y-6 py-8">
        <div>
          <h3 className="font-semibold text-lg mb-2">
            Step 1: Authenticate with Google
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            To connect your Google Business Profile, you must grant DViO access
            through Google's secure OAuth flow.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="disabled:pointer-events-none disabled:opacity-50"
            type="button"
            onClick={() => initiateConnection(false)}
            disabled={
              loading ||
              !!secretId ||
              connectionStatus?.connection_status === 'pending'
            }
            aria-label="Authenticate"
          >
            <img src={googleCTA} alt="Continue with Google" />
          </button>
          {(secretId ||
            connectionStatus?.connection_status !== 'incomplete') && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckIcon className="size-5" />
              <span className="text-sm font-medium">Authenticated</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEstablishConnectionStep = () => (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">Step 2: Complete Connection</h3>
        <p className="text-sm text-muted-foreground">
          Click "Connect Account" to establish the connection and begin data
          synchronization
        </p>
      </div>
      <div className="flex justify-center pt-4">
        <Button
          variant="outline"
          type="button"
          onClick={
            isReauthFlow
              ? () => reestablishConnection()
              : () => establishConnection()
          }
          disabled={loading || !secretId || success}
          aria-label={
            isReauthFlow ? 'Complete Re-authentication' : 'Connect Account'
          }
          className="w-full max-w-xs"
        >
          {loading
            ? 'Connecting...'
            : isReauthFlow
              ? 'Complete Re-authentication'
              : 'Connect Account'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <title>Google Business Profile | DViO One</title>
      <meta
        name="description"
        content="Google Business Profile Page Connector DViO ONE!"
      />

      <HeaderLayout>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/team/connections">Connectors</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="flex items-center gap-2">
                <Avatar className="size-6 rounded-none">
                  <AvatarImage
                    src={googleBusinessProfile}
                    className="object-contain"
                  />
                </Avatar>
                Google Business Profile
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </HeaderLayout>

      <div className="w-full lg:h-[calc(100vh-4rem)] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-full">
          <div className="lg:col-span-6 flex flex-col">
            <Card className="flex flex-col overflow-hidden transition-shadow hover:shadow-lg">
              <CardHeader className="shrink-0 pb-3">
                <CardTitle className="text-lg font-semibold">
                  Connection Setup
                </CardTitle>
                <CardDescription className="text-sm">
                  {connectionStatus?.text}
                </CardDescription>
              </CardHeader>
              <Separator />
              <CardContent className="flex-1 overflow-auto pt-6">
                {(connectionStatus?.connection_status === 'complete' &&
                  !isReauthFlow) ||
                success ? (
                  <ConnectionSuccessSection>
                    <Button
                      variant={'default'}
                      className="inline-flex items-center gap-2 h-9"
                      type="button"
                      onClick={() => initiateConnection(true)}
                      disabled={loading}
                      aria-label="Re-authenticate"
                    >
                      <RefreshCcw
                        className={`size-4 ${
                          loading &&
                          sessionStorage.getItem('reauth_google_business') ===
                            'true'
                            ? 'animate-spin hidden'
                            : ''
                        }`}
                      />
                      {loading &&
                      sessionStorage.getItem('reauth_google_business') ===
                        'true' ? (
                        <>
                          <Spinner className="mr-2" />
                          Connecting...
                        </>
                      ) : (
                        'Re-Authenticate'
                      )}
                    </Button>
                  </ConnectionSuccessSection>
                ) : (
                  <>
                    {!success ? (
                      <>
                        {secretId
                          ? renderEstablishConnectionStep()
                          : renderAuthenticationStep()}
                      </>
                    ) : (
                      <ConnectionSuccessSection>
                        <Button
                          variant={'default'}
                          className="inline-flex items-center gap-2 h-9"
                          type="button"
                          onClick={() => initiateConnection(true)}
                          disabled={loading}
                          aria-label="Re-authenticate"
                        >
                          <RefreshCcw
                            className={`size-4 ${
                              loading &&
                              sessionStorage.getItem('reauth_google_business') ===
                                'true'
                                ? 'animate-spin hidden'
                                : ''
                            }`}
                          />
                          {loading &&
                          sessionStorage.getItem('reauth_google_business') ===
                            'true' ? (
                            <>
                              <Spinner className="mr-2" />
                              Connecting...
                            </>
                          ) : (
                            'Re-Authenticate'
                          )}
                        </Button>
                      </ConnectionSuccessSection>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-6 flex flex-col">
            <Card className="flex-1 flex flex-col overflow-hidden transition-shadow hover:shadow-lg">
              <CardHeader className="shrink-0 pb-3">
                <CardTitle className="text-lg font-semibold">
                  Documentation
                </CardTitle>
                <CardDescription className="text-sm">
                  Setup guide and data catalogue
                </CardDescription>
              </CardHeader>
              <Separator />
              <CardContent className="flex-1 overflow-auto pt-4">
                <ConnectorDocumentation
                  setupGuide={googleBusinessProfileSetupGuide}
                  catalogue={googleBusinessProfileCatalogue}
                  catalogueDescription="Explore Google Business Profile metrics to be synced"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
